/** A failed invariant, distinguishable from any other throw so an error boundary can label it. */
export class InvariantError extends Error {
  override readonly name = "InvariantError";
}

/** Assert a condition that MUST hold. The message is a developer statement — it is never rendered to a user and
 *  never beaconed, because it is free text and every telemetry label in this app comes from a closed union. */
export function assert(condition: unknown, message: string): asserts condition {
  if (!condition) throw new InvariantError(message);
}

/** Narrow away `null | undefined` at a boundary where absence is a bug rather than a state. Use it sparingly:
 *  on this surface absence is usually a REAL state (an unknown balance, a pending identity, a seed that has not
 *  landed) and those must be rendered, not asserted away. */
export function assertPresent<T>(value: T | null | undefined, message: string): T {
  if (value == null) throw new InvariantError(message);
  return value;
}

/** Exhaustiveness. `default: return never(x)` makes adding a union member a COMPILE error at every switch that
 *  handles it, which is the only mechanism that keeps a closed set — a route label, a toast kind, a pre-sign
 *  reason — actually closed. */
export function never(value: never, message = "unreachable"): never {
  throw new InvariantError(`${message}: ${JSON.stringify(value)}`);
}
